import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS, ja } from "date-fns/locale";
import { ReactComponent as IconMaintenanceRefuel } from "./icon-maintenance-refuel.svg";
import { ReactComponent as IconMaintenanceAdblue } from "./icon-maintenanceitem-adblue.svg";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import RecordRTC from "recordrtc";
import { DataDialog } from "@app/components/atoms";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";
import { IconWarningFormEdit } from "@app/components/icons";
import { REGEX } from "@app/helpers";
import { MediaUpload } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/MediaUpload/MediaUpload";
import {
  isWorkAtInValidRange,
  submitCreateRefuel,
  submitUpdateRefuel,
  WORK_AT_MIN_DATE,
  type ExistingRecordingMeta,
} from "./createRefuelSubmit.helper";
import {
  setIsLoading,
  setIsUpdatedMachineReportResponses,
  setShowAlert,
} from "@app/store";
import "@app/pages/Features/MachineDetail/Components/InspectionHistory/recording.css";
import { SignatureDialog } from "../SignatureDialog";
import { getGroupIsSignatureRequired } from "../groupSignature.helper";
import { useAppContext } from "@app/AppContext";

const REFUEL_MEDIA_TOTAL_LIMIT = 5;
const RECORDING_TARGET_ID = "create-refuel";
const NUMERIC_SMR_MAX = 999999.9;
const NUMERIC_ODO_MAX = 999999.9;
const FUEL_AMOUNT_MAX = 99999.9;
const ADBLUE_AMOUNT_MAX = 999999.9;

const dateLocales: Record<string, typeof enUS> = {
  en: enUS,
  ja,
};
const NUMERIC_INPUT_PATTERN = /^\d*\.?\d{0,1}$/;

function stripLeadingZerosFromNumeric(value: string): string {
  if (!value) return value;
  if (value.startsWith("0.")) return value;

  if (value.includes(".")) {
    const [intPart, ...decParts] = value.split(".");
    const decPart = decParts.join("").replace(/\./g, "");
    const strippedInt = intPart.replace(/^0+/, "") || "0";
    if (value.endsWith(".") && decPart === "") {
      return `${strippedInt}.`;
    }
    return `${strippedInt}.${decPart}`;
  }

  return value.replace(/^0+/, "") || "0";
}

function enforceNumericDecimalFormat(value: string): string {
  const dotIndex = value.indexOf(".");
  if (dotIndex === -1) return value;

  const intPart = value.slice(0, dotIndex);
  const decPart = value.slice(dotIndex + 1).replace(/\./g, "");
  if (value.endsWith(".") && decPart === "") {
    return `${intPart}.`;
  }
  return `${intPart}.${decPart.slice(0, 1)}`;
}

function sanitizeNumericInputValue(raw: string, fallback: string): string {
  if (!NUMERIC_INPUT_PATTERN.test(raw)) {
    return fallback;
  }
  const withoutLeadingZeros = stripLeadingZerosFromNumeric(raw);
  return enforceNumericDecimalFormat(withoutLeadingZeros);
}

function isNumericValueValid(value: string, max: number): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.endsWith(".")) {
    return false;
  }
  if (!/^\d+\.\d$/.test(trimmed) && !/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
}

function normalizeNumericOnBlur(value: string, max: number): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  let normalized = enforceNumericDecimalFormat(
    stripLeadingZerosFromNumeric(trimmed),
  );
  if (/^\d+$/.test(normalized)) {
    normalized = `${normalized}.0`;
  } else if (normalized.endsWith(".")) {
    normalized = `${normalized}0`;
  }

  const parsed = parseFloat(normalized);
  if (Number.isFinite(parsed) && parsed > max) {
    return String(max);
  }

  return normalized;
}

function isTextFieldValueInvalid(value: string): boolean {
  return (
    !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
    !REGEX.emojiRegex.test(value)
  );
}

function stripLabelRequiredMarker(label: string): string {
  return label.replace(/\s*\*+\s*$/, "").trim();
}

function getOdometerLabel(t: (key: string) => string): string {
  return stripLabelRequiredMarker(t("odometer"));
}

function cloneBlobForPlayback(blob: Blob): Blob {
  const type =
    blob.type && blob.type !== "application/octet-stream"
      ? blob.type
      : "audio/wav";
  if (blob.type === type) return blob;
  return new Blob([blob], { type });
}

function ItemBlobAudio({
  blob,
  onRemove,
}: {
  blob: Blob;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!blob?.size) {
      setSrc("");
      return;
    }
    const playable = cloneBlobForPlayback(blob);
    const url = URL.createObjectURL(playable);
    setSrc(url);
    const el = audioRef.current;
    if (el) {
      el.src = url;
      el.load();
    }
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  if (!src) return null;

  return (
    <div style={{ marginTop: "12px", touchAction: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <audio ref={audioRef} controls src={src} style={{ flex: 1 }} />
        <Tooltip title={t("formAction.delete")}>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: "#d32f2f",
              marginLeft: 2,
              borderRadius: "50%",
              transition: "all 0.3s ease",
              "&:hover": {
                border: "2px solid #d32f2f",
                backgroundColor: "transparent",
              },
            }}
          >
            <IconDeleteRed />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}

type RefuelPrefillMedia = {
  fileName?: string;
  mediaUrl?: string;
  mimeType?: string;
  captureMediaAt?: string;
  filePath?: string;
  thumbnailUrl?: string;
};

export type RefuelPrefill = {
  mode?: "create" | "update";
  machineReportId?: string;
  comment?: string | null;
  serviceMeter?: number | string | null;
  odometer?: number | string | null;
  workAt?: string | null;
  isFuelRefilled?: boolean;
  fuelAmount?: number | string | null;
  isAdblueRefilled?: boolean;
  adblueAmount?: number | string | null;
  machineReportMedias?: RefuelPrefillMedia[] | null;
  machineReportRecordings?: Array<{
    recordingUrl?: string | null;
    fileName?: string | null;
    filePath?: string | null;
    mimeType?: string | null;
    recordingAt?: string | null;
  }> | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  groupId: string;
  machineId: string;
  prefill?: RefuelPrefill | null;
  onSuccess?: () => void;
};

const initialFormState = () => ({
  comment: "",
  commentError: false,
  recordedBlob: null as Blob | null,
  mediaFiles: [] as File[],
  fileCount: 0,
  hasUploadError: false,
  removedMediaUrls: [] as string[],
  serviceMeterValue: "",
  serviceMeterTouched: false,
  odometerValue: "",
  odometerTouched: false,
  workAt: new Date() as Date | null,
  workAtTouched: false,
  fuelSwitchOn: true,
  fuelAmountValue: "",
  fuelAmountTouched: false,
  adblueSwitchOn: false,
  adblueAmountValue: "",
  adblueAmountTouched: false,
});

function RefuelSwitchRow({
  icon,
  label,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {icon}
        <Typography
          variant="body1"
          className="txt-black-1"
          sx={{ fontWeight: 500 }}
        >
          {label}
        </Typography>
      </Box>
      <Switch
        className={`btn-switch ${checked ? "checked" : ""}`}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </Box>
  );
}

function toPrefillMediaFiles(
  medias?: RefuelPrefillMedia[] | null,
): File[] {
  if (!medias?.length) return [];
  return medias.map((media) =>
    ({
      fileName: media.fileName || "",
      name: media.fileName || "",
      mediaUrl: media.mediaUrl || "",
      mimeType: media.mimeType || "",
      type: media.mimeType || "",
      captureMediaAt: media.captureMediaAt,
      filePath: media.filePath || "",
      thumbnailUrl: media.thumbnailUrl || "",
    }) as unknown as File,
  );
}

function formatPrefillNumeric(value?: number | string | null): string {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

export function CreateRefuel({
  open,
  onClose,
  groupId,
  machineId,
  prefill = null,
  onSuccess,
}: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { userTimeFormat } = useAppContext();
  const dateLocale = dateLocales[i18n.language] ?? enUS;
  const [form, setForm] = useState(initialFormState);
  const [mediaPreviewNonce, setMediaPreviewNonce] = useState(0);

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<ReturnType<typeof RecordRTC> | null>(
    null,
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [recordingInterval, setRecordingInterval] = useState<ReturnType<
    typeof setInterval
  > | null>(null);
  const [mediaAvailable, setMediaAvailable] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const stoppedRef = useRef(false);
  const skipNextRecordingBlobRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<ReturnType<typeof RecordRTC> | null>(null);
  const existingRecordingRef = useRef<ExistingRecordingMeta | null>(null);
  const recordingReplacedRef = useRef(false);

  const [openFormNoMic, setOpenFormNoMic] = useState(false);
  const [openFormDeleteRecording, setOpenFormDeleteRecording] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const patchForm = useCallback(
    (patch: Partial<ReturnType<typeof initialFormState>>) => {
      setForm((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const releaseMicrophone = useCallback(() => {
    const stopStreamTracks = (stream: MediaStream | null | undefined) => {
      stream?.getTracks?.().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    };
    stopStreamTracks(mediaStreamRef.current);
    stopStreamTracks(recorderRef.current?.stream);
    mediaStreamRef.current = null;
  }, []);

  const resetRecordingGlobals = useCallback(() => {
    if (recordingInterval) clearInterval(recordingInterval);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    releaseMicrophone();
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    setRecorder(null);
    recorderRef.current = null;
    setRecordingInterval(null);
    setMediaAvailable(true);
    setRecordingActive(false);
    recordingStartTimeRef.current = null;
    stoppedRef.current = false;
  }, [recordingInterval, releaseMicrophone]);

  const isUpdateMode = prefill?.mode === "update";

  const applyPrefill = useCallback(async () => {
    if (!prefill) {
      return;
    }

    const mediaFiles = toPrefillMediaFiles(prefill.machineReportMedias);
    const existingRecording = prefill.machineReportRecordings?.[0] || null;
    existingRecordingRef.current = existingRecording
      ? {
          recordingUrl: existingRecording.recordingUrl,
          fileName: existingRecording.fileName,
          filePath: existingRecording.filePath,
          mimeType: existingRecording.mimeType,
          recordingAt: existingRecording.recordingAt,
        }
      : null;
    recordingReplacedRef.current = false;

    let recordedBlob: Blob | null = null;
    const recordingUrl = existingRecording?.recordingUrl;
    if (recordingUrl) {
      try {
        const response = await fetch(recordingUrl);
        if (response.ok) {
          recordedBlob = await response.blob();
        }
      } catch (error) {
        console.error("Failed to load existing refuel recording:", error);
      }
    }

    const fuelOn = prefill.isFuelRefilled ?? prefill.fuelAmount != null;
    const adblueOn = prefill.isAdblueRefilled ?? prefill.adblueAmount != null;

    setForm({
      ...initialFormState(),
      comment: prefill.comment?.trim() || "",
      mediaFiles,
      fileCount: mediaFiles.length,
      recordedBlob,
      serviceMeterValue: formatPrefillNumeric(prefill.serviceMeter),
      odometerValue: formatPrefillNumeric(prefill.odometer),
      workAt: prefill.workAt ? new Date(prefill.workAt) : new Date(),
      fuelSwitchOn: fuelOn,
      fuelAmountValue: formatPrefillNumeric(prefill.fuelAmount),
      adblueSwitchOn: adblueOn,
      adblueAmountValue: formatPrefillNumeric(prefill.adblueAmount),
    });
    setMediaPreviewNonce((n) => n + 1);
  }, [prefill]);

  const resetForm = useCallback(() => {
    setForm(initialFormState());
    setMediaPreviewNonce((n) => n + 1);
    resetRecordingGlobals();
  }, [resetRecordingGlobals]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !prefill) return;
    void applyPrefill();
  }, [open, prefill, applyPrefill]);

  useEffect(() => {
    recorderRef.current = recorder;
  }, [recorder]);

  const stopRecording = useCallback(() => {
    const activeRecorder = recorderRef.current;
    if (!activeRecorder) {
      releaseMicrophone();
      return;
    }

    stoppedRef.current = true;
    setIsRecording(false);
    setIsPaused(false);
    if (recordingInterval) clearInterval(recordingInterval);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    releaseMicrophone();

    activeRecorder.stopRecording(() => {
      window.setTimeout(() => {
        const raw = activeRecorder.getBlob() as Blob | undefined;
        const skip = skipNextRecordingBlobRef.current;
        skipNextRecordingBlobRef.current = false;

        if (!skip && raw && raw.size > 44) {
          recordingReplacedRef.current = true;
          patchForm({ recordedBlob: cloneBlobForPlayback(raw) });
        }

        setRecorder(null);
        recorderRef.current = null;
        recordingStartTimeRef.current = null;
        setRecordingActive(false);
        setRecordingTime(0);
      }, 300);
    });
  }, [recordingInterval, releaseMicrophone, patchForm]);

  useEffect(() => {
    if (recordingTime >= 60 && isRecording && !stoppedRef.current) {
      stopRecording();
    }
  }, [recordingTime, isRecording, stopRecording]);

  const handleClose = useCallback(() => {
    if (isRecording || isPaused || recorderRef.current) {
      stopRecording();
    }
    setOpenCancelConfirm(false);
    setSignatureDialogOpen(false);
    setViewerOpen(false);
    setSelectedImage("");
    resetForm();
    onClose();
  }, [isPaused, isRecording, onClose, resetForm, stopRecording]);

  const handleRequestClose = useCallback(() => {
    setOpenCancelConfirm(true);
  }, []);

  const startRecording = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMic = devices.some((device) => device.kind === "audioinput");

      if (!hasMic) {
        setOpenFormNoMic(true);
        return;
      }

      releaseMicrophone();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      patchForm({ recordedBlob: null });
      recordingReplacedRef.current = true;
      setRecordingActive(true);
      setIsPaused(false);
      stoppedRef.current = false;

      const newRecorder = new RecordRTC(stream, {
        type: "audio",
        mimeType: "audio/webm",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        audioBitsPerSecond: 64000,
      });

      recorderRef.current = newRecorder;
      recordingStartTimeRef.current = Date.now();
      newRecorder.startRecording();
      setRecorder(newRecorder);
      setIsRecording(true);
      setRecordingTime(0);

      const interval = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - recordingStartTimeRef.current) / 1000,
          );
          setRecordingTime(Math.min(elapsed, 60));
        }
      }, 1000);

      setRecordingInterval(interval);

      recordingTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 59200);
    } catch (err) {
      console.warn("Microphone access denied", err);
      releaseMicrophone();
      setMediaAvailable(false);
      setRecordingActive(false);
    }
  };

  const handleDeleteRecordingConfirmed = useCallback(() => {
    if (isRecording || isPaused) {
      skipNextRecordingBlobRef.current = true;
      stopRecording();
    }

    releaseMicrophone();

    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

    patchForm({ recordedBlob: null });
    recordingReplacedRef.current = true;
    existingRecordingRef.current = null;
    setRecordingTime(0);
    setIsPaused(false);
    setIsRecording(false);
    setRecorder(null);
    recorderRef.current = null;
    setRecordingInterval(null);
    setMediaAvailable(true);
    setRecordingActive(false);
    setOpenFormDeleteRecording(false);
    recordingStartTimeRef.current = null;
  }, [isRecording, isPaused, stopRecording, releaseMicrophone, patchForm]);

  const uploadDescription = useMemo(
    () => [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ],
    [t],
  );

  const recordStartDisabled =
    !mediaAvailable ||
    isRecording ||
    isPaused ||
    !!form.recordedBlob ||
    recordingTime >= 60;

  const serviceMeterVal = form.serviceMeterValue.trim();
  const odometerVal = form.odometerValue.trim();
  const serviceMeterOverMax =
    !!serviceMeterVal &&
    Number.isFinite(parseFloat(serviceMeterVal)) &&
    parseFloat(serviceMeterVal) > NUMERIC_SMR_MAX;
  const serviceMeterFormatInvalid =
    !!serviceMeterVal && !isNumericValueValid(serviceMeterVal, NUMERIC_SMR_MAX);
  const serviceMeterMissing = !serviceMeterVal;
  const serviceMeterShowError =
    form.serviceMeterTouched &&
    (serviceMeterMissing || serviceMeterOverMax || serviceMeterFormatInvalid);
  const serviceMeterHelperText = serviceMeterOverMax
    ? t("createInspection.numericMax", { max: NUMERIC_SMR_MAX })
    : undefined;

  const serviceMeterInvalid =
    serviceMeterMissing || serviceMeterFormatInvalid || serviceMeterOverMax;

  const odometerOverMax =
    !!odometerVal &&
    Number.isFinite(parseFloat(odometerVal)) &&
    parseFloat(odometerVal) > NUMERIC_ODO_MAX;
  const odometerFormatInvalid =
    !!odometerVal && !isNumericValueValid(odometerVal, NUMERIC_ODO_MAX);
  const odometerShowError =
    form.odometerTouched && (odometerOverMax || odometerFormatInvalid);
  const odometerHelperText = odometerOverMax
    ? t("createInspection.numericMax", { max: NUMERIC_ODO_MAX })
    : undefined;

  const odometerInvalid = odometerFormatInvalid || odometerOverMax;

  const fuelAmountVal = form.fuelAmountValue.trim();
  const fuelAmountMissing = form.fuelSwitchOn && !fuelAmountVal;
  const fuelAmountOverMax =
    !!fuelAmountVal &&
    Number.isFinite(parseFloat(fuelAmountVal)) &&
    parseFloat(fuelAmountVal) > FUEL_AMOUNT_MAX;
  const fuelAmountFormatInvalid =
    form.fuelSwitchOn &&
    !!fuelAmountVal &&
    !isNumericValueValid(fuelAmountVal, FUEL_AMOUNT_MAX);
  const fuelAmountShowError =
    form.fuelAmountTouched &&
    (fuelAmountMissing || fuelAmountOverMax || fuelAmountFormatInvalid);
  const fuelAmountHelperText = fuelAmountOverMax
    ? t("createInspection.numericMax", { max: FUEL_AMOUNT_MAX })
    : undefined;
  const fuelAmountInvalid =
    form.fuelSwitchOn &&
    (fuelAmountMissing || fuelAmountFormatInvalid || fuelAmountOverMax);

  const adblueAmountVal = form.adblueAmountValue.trim();
  const adblueAmountOverMax =
    !!adblueAmountVal &&
    Number.isFinite(parseFloat(adblueAmountVal)) &&
    parseFloat(adblueAmountVal) > ADBLUE_AMOUNT_MAX;
  const adblueAmountFormatInvalid =
    form.adblueSwitchOn &&
    !!adblueAmountVal &&
    !isNumericValueValid(adblueAmountVal, ADBLUE_AMOUNT_MAX);
  const adblueAmountShowError =
    form.adblueAmountTouched &&
    (adblueAmountOverMax || adblueAmountFormatInvalid);
  const adblueAmountHelperText = adblueAmountOverMax
    ? t("createInspection.numericMax", { max: ADBLUE_AMOUNT_MAX })
    : undefined;
  const adblueAmountInvalid =
    form.adblueSwitchOn && (adblueAmountFormatInvalid || adblueAmountOverMax);

  const workAtMissing = !form.workAt;
  const workAtOutOfRange = !!form.workAt && !isWorkAtInValidRange(form.workAt);
  const workAtInvalid = workAtMissing || workAtOutOfRange;
  const workAtShowError = form.workAtTouched && workAtInvalid;
  const workAtHelperText = workAtMissing
    ? t("required")
    : workAtOutOfRange
    ? t("createRefuel.workAtOutOfRange")
    : undefined;

  const bothRefillSwitchesOff = !form.fuelSwitchOn && !form.adblueSwitchOn;

  const isRegisterDisabled =
    isSubmitting ||
    !groupId ||
    !machineId ||
    bothRefillSwitchesOff ||
    workAtInvalid ||
    form.commentError ||
    form.hasUploadError ||
    fuelAmountInvalid ||
    adblueAmountInvalid ||
    serviceMeterInvalid ||
    odometerInvalid ||
    isRecording ||
    isPaused;

  const handleSignatureClose = useCallback(() => {
    setSignatureDialogOpen(false);
  }, []);

  const handleSignatureSend = useCallback(
    async (
      signatureDataUrl: string | null,
      options?: { submitAlreadyStarted?: boolean },
    ) => {
      if (!groupId || !machineId) return;

      if (!options?.submitAlreadyStarted) {
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        setIsSubmitting(true);
      }

      dispatch(setIsLoading(true));

      try {
        const draft = {
          comment: form.comment,
          mediaFiles: form.mediaFiles,
          recordedBlob: form.recordedBlob,
          serviceMeterValue: form.serviceMeterValue,
          odometerValue: form.odometerValue,
          workAt: form.workAt,
          fuelSwitchOn: form.fuelSwitchOn,
          fuelAmountValue: form.fuelAmountValue,
          adblueSwitchOn: form.adblueSwitchOn,
          adblueAmountValue: form.adblueAmountValue,
        };

        const { successMessage } =
          isUpdateMode && prefill?.machineReportId
            ? await submitUpdateRefuel({
                groupId,
                machineId,
                machineReportId: prefill.machineReportId,
                draft,
                signatureDataUrl,
                existingRecording: existingRecordingRef.current,
                keepExistingRecording:
                  !recordingReplacedRef.current &&
                  !!existingRecordingRef.current,
              })
            : await submitCreateRefuel({
                groupId,
                machineId,
                draft,
                signatureDataUrl,
              });

        dispatch(
          setShowAlert(successMessage || t("createRefuel.submitSuccess")),
        );
        dispatch(setIsUpdatedMachineReportResponses(`${Date.now()}`));
        setSignatureDialogOpen(false);
        onSuccess?.();
        handleClose();
      } catch (error) {
        console.error(
          isUpdateMode
            ? "Failed to update refuel report:"
            : "Failed to create refuel report:",
          error,
        );
        const message =
          error instanceof AxiosError
            ? error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage
            : error instanceof Error && error.message === "SIGNATURE_REQUIRED"
            ? t("createInspection.signature")
            : error instanceof Error &&
              error.message === "SIGNATURE_UPLOAD_FAILED"
            ? t("Anunexpectederroroccurredwhileprocessingthefile")
            : null;
        dispatch(
          setShowAlert(
            message || t("Anunexpectederroroccurredwhileprocessingthefile"),
          ),
        );
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        dispatch(setIsLoading(false));
      }
    },
    [
      dispatch,
      form,
      groupId,
      handleClose,
      isUpdateMode,
      machineId,
      onSuccess,
      prefill?.machineReportId,
      t,
    ],
  );

  const handleOpenSignatureDialog = useCallback(async () => {
    if (isRegisterDisabled || !groupId || !machineId) return;
    if (submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      if (isRecording || isPaused || recorderRef.current) {
        stopRecording();
      }

      const isSignatureRequired = await getGroupIsSignatureRequired(groupId);
      if (isSignatureRequired) {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        setSignatureDialogOpen(true);
        return;
      }

      await handleSignatureSend(null, { submitAlreadyStarted: true });
    } catch (error) {
      console.error("Failed to open signature dialog:", error);
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    groupId,
    handleSignatureSend,
    isPaused,
    isRecording,
    isRegisterDisabled,
    machineId,
    stopRecording,
  ]);

  const handleUploadPreview = (url: string | string[]) => {
    const u = Array.isArray(url) ? url[0] : url;
    setSelectedImage(u);
    setViewerOpen(true);
  };

  return (
    <>
      <DataDialog
        open={open}
        onClose={handleRequestClose}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {t("refuel")}

            <span style={{ color: "grey", fontSize: "12px", fontWeight: 500 }}>
              *{t("required")}
            </span>
          </div>
        }
        fullWidth
        disableEnforceFocus
        className="dialog-container"
        dialogContentSx={{
          overflow: "auto",
          maxHeight: "min(70vh, 720px)",
          touchAction: "auto",
        }}
        dialogActions={
          <>
            <Button
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "0px" }}
              onClick={handleRequestClose}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              style={{ width: "auto" }}
              onClick={handleOpenSignatureDialog}
              disabled={isRegisterDisabled}
            >
              {isUpdateMode ? t("formAction.update") : t("register")}
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}>
          <RefuelSwitchRow
            icon={<IconMaintenanceRefuel width={24} height={24} aria-hidden />}
            label={t("dialogRefuel.fuelRefill")}
            checked={form.fuelSwitchOn}
            onChange={(checked) =>
              patchForm({
                fuelSwitchOn: checked,
                ...(checked
                  ? {}
                  : { fuelAmountValue: "", fuelAmountTouched: false }),
              })
            }
          />

          {form.fuelSwitchOn && (
            <TextField
              className={`input-field ${
                fuelAmountShowError ? "error-border" : ""
              }`}
              label={t("dialogMaintenanceReports.refillAmount")}
              type="text"
              inputMode="decimal"
              fullWidth
              variant="outlined"
              required
              value={form.fuelAmountValue}
              onChange={(e) => {
                const validatedInput = sanitizeNumericInputValue(
                  e.target.value,
                  form.fuelAmountValue,
                );
                patchForm({
                  fuelAmountValue: validatedInput,
                  fuelAmountTouched: true,
                });
              }}
              onBlur={() => {
                const normalized = normalizeNumericOnBlur(
                  form.fuelAmountValue,
                  FUEL_AMOUNT_MAX,
                );
                patchForm({
                  fuelAmountTouched: true,
                  ...(normalized !== form.fuelAmountValue
                    ? { fuelAmountValue: normalized }
                    : {}),
                });
              }}
              error={fuelAmountShowError}
              helperText={fuelAmountHelperText}
              FormHelperTextProps={{
                className: fuelAmountShowError ? "error-text" : undefined,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">L</InputAdornment>,
              }}
              sx={{
                "& .MuiOutlinedInput-input": {
                  textAlign: "right",
                  direction: "ltr",
                  paddingRight: "8px",
                },
              }}
            />
          )}

          <RefuelSwitchRow
            icon={<IconMaintenanceAdblue width={24} height={24} aria-hidden />}
            label="AdBlue®"
            checked={form.adblueSwitchOn}
            onChange={(checked) =>
              patchForm({
                adblueSwitchOn: checked,
                ...(checked
                  ? {}
                  : { adblueAmountValue: "", adblueAmountTouched: false }),
              })
            }
          />

          {form.adblueSwitchOn && (
            <TextField
              className={`input-field ${
                adblueAmountShowError ? "error-border" : ""
              }`}
              label={t("dialogMaintenanceReports.refillAmount")}
              type="text"
              inputMode="decimal"
              fullWidth
              variant="outlined"
              value={form.adblueAmountValue}
              onChange={(e) => {
                const validatedInput = sanitizeNumericInputValue(
                  e.target.value,
                  form.adblueAmountValue,
                );
                patchForm({
                  adblueAmountValue: validatedInput,
                  adblueAmountTouched: true,
                });
              }}
              onBlur={() => {
                const normalized = normalizeNumericOnBlur(
                  form.adblueAmountValue,
                  ADBLUE_AMOUNT_MAX,
                );
                patchForm({
                  adblueAmountTouched: true,
                  ...(normalized !== form.adblueAmountValue
                    ? { adblueAmountValue: normalized }
                    : {}),
                });
              }}
              error={adblueAmountShowError}
              helperText={adblueAmountHelperText}
              FormHelperTextProps={{
                className: adblueAmountShowError ? "error-text" : undefined,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">L</InputAdornment>,
              }}
              sx={{
                "& .MuiOutlinedInput-input": {
                  textAlign: "right",
                  direction: "ltr",
                  paddingRight: "8px",
                },
              }}
            />
          )}

          <div
            className="wrapper-text-field mt-8"
            style={{ position: "relative" }}
          >
            <TextField
              multiline
              rows={3}
              className="input-field"
              label={t("dialogReport.comment")}
              value={form.comment}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              onChange={(e) => {
                const value = e.target.value;
                patchForm({
                  comment: value,
                  commentError: isTextFieldValueInvalid(value),
                });
              }}
              error={form.commentError}
              helperText={form.commentError ? t("invalidCharacters") : ""}
              InputProps={{
                style: { paddingRight: "65px" },
              }}
            />

            {mediaAvailable && !form.recordedBlob && (
              <div
                style={{
                  position: "absolute",
                  right: 12,
                  top: 40,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                {recordingActive && (isRecording || isPaused) ? (
                  <Tooltip title={t("stop")}>
                    <StopIcon
                      onClick={stopRecording}
                      sx={{
                        color: "#d32f2f",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#a30000",
                          transform: "scale(1.1)",
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("start")}>
                    <FiberManualRecordIcon
                      onClick={recordStartDisabled ? undefined : startRecording}
                      sx={{
                        color: recordStartDisabled ? "#ccc" : "#d32f2f",
                        cursor: recordStartDisabled ? "not-allowed" : "pointer",
                        pointerEvents: recordStartDisabled ? "none" : "auto",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: recordStartDisabled ? "#ccc" : "#a30000",
                          transform: recordStartDisabled
                            ? "none"
                            : "scale(1.1)",
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          {isRecording && recordingActive && recordingTime < 60 && (
            <Tooltip title={t("recording")} arrow>
              <div
                className="glow-red"
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  color: "#d32f2f",
                  backgroundColor: "#ffe6e6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  boxShadow: "0 0 8px #f44336",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  width: "60px",
                }}
              >
                <FiberManualRecordIcon fontSize="small" />
                <span style={{ transform: "translateY(1px)" }}>
                  {`${recordingTime}s`}
                </span>
              </div>
            </Tooltip>
          )}

          {isPaused && recordingActive && (
            <Tooltip title={t("paused")} arrow>
              <div
                className="glow-yellow"
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  color: "#f9a825",
                  backgroundColor: "#fff8e1",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  boxShadow: "0 0 8px #fbc02d",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: "32px",
                  width: "60px",
                }}
              >
                <PauseCircleIcon fontSize="small" />
                {`${recordingTime}s`}
              </div>
            </Tooltip>
          )}

          {form.recordedBlob && (
            <ItemBlobAudio
              blob={form.recordedBlob}
              onRemove={() => setOpenFormDeleteRecording(true)}
            />
          )}

          {recordingActive && (isRecording || isPaused) && (
            <div
              className="warning flex justify-content-start"
              style={{ marginBottom: 12 }}
            >
              <IconWarningFormEdit />
              <span style={{ marginLeft: 12, flex: 1 }} className="txt-black-1">
                {t("warningUpdateItem3")}
              </span>
            </div>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <div className="update-media-upload-file mt-12 w-100">
              <MediaUpload
                key={`${RECORDING_TARGET_ID}-${mediaPreviewNonce}`}
                id={RECORDING_TARGET_ID}
                setFileCount={(n: number | ((prev: number) => number)) =>
                  patchForm({
                    fileCount: typeof n === "function" ? n(form.fileCount) : n,
                  })
                }
                medias={form.mediaFiles}
                setHasUploadError={(err: boolean) =>
                  patchForm({ hasUploadError: err })
                }
                showMaxUploadLimit={true}
                errMessage={
                  form.mediaFiles.length > REFUEL_MEDIA_TOTAL_LIMIT
                    ? t("maxFilesExceeded", {
                        max: REFUEL_MEDIA_TOTAL_LIMIT,
                      })
                    : ""
                }
                setMedias={(files: File[]) => {
                  const maxForForm = Math.min(
                    5,
                    Math.max(0, REFUEL_MEDIA_TOTAL_LIMIT),
                  );
                  const cappedFiles = files.slice(0, maxForForm);

                  const existingFiles = form.mediaFiles.map(
                    (
                      file: File & {
                        filePath?: string;
                        mediaUrl?: string;
                      },
                    ) => file.filePath || file.mediaUrl || "",
                  );
                  const newFiles = cappedFiles.map(
                    (
                      file: File & {
                        filePath?: string;
                        mediaUrl?: string;
                      },
                    ) => file.filePath || file.mediaUrl || "",
                  );
                  const removedFiles = existingFiles.filter(
                    (mediaUrl: string) =>
                      mediaUrl && !newFiles.includes(mediaUrl),
                  );
                  let removedMediaUrls = form.removedMediaUrls;
                  if (removedFiles.length > 0) {
                    removedMediaUrls = [
                      ...form.removedMediaUrls,
                      ...removedFiles,
                    ];
                  }
                  patchForm({
                    mediaFiles: cappedFiles,
                    fileCount: cappedFiles.length,
                    removedMediaUrls,
                  });
                }}
                onPreviewMedia={handleUploadPreview}
                uploadDescription={uploadDescription}
                maxFiles={5}
              />
            </div>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              "& .MuiFormControl-root": {
                width: "100%",
              },
            }}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={dateLocale}
            >
              <DateTimePicker
                label={t("dialogMaintenanceReports.workTime")}
                className="input-field"
                format={userTimeFormat}
                value={form.workAt ?? undefined}
                minDateTime={WORK_AT_MIN_DATE}
                maxDateTime={new Date()}
                onChange={(value) =>
                  patchForm({ workAt: value, workAtTouched: true })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: workAtShowError,
                    helperText: workAtHelperText,
                    onBlur: () => patchForm({ workAtTouched: true }),
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              className={`input-field ${
                serviceMeterShowError ? "error-border" : ""
              }`}
              label={t("serviceMeterOrSMR")}
              type="text"
              inputMode="decimal"
              fullWidth
              variant="outlined"
              required
              value={form.serviceMeterValue}
              onChange={(e) => {
                const validatedInput = sanitizeNumericInputValue(
                  e.target.value,
                  form.serviceMeterValue,
                );
                patchForm({
                  serviceMeterValue: validatedInput,
                  serviceMeterTouched: true,
                });
              }}
              onBlur={() => {
                const normalized = normalizeNumericOnBlur(
                  form.serviceMeterValue,
                  NUMERIC_SMR_MAX,
                );
                patchForm({
                  serviceMeterTouched: true,
                  ...(normalized !== form.serviceMeterValue
                    ? { serviceMeterValue: normalized }
                    : {}),
                });
              }}
              error={serviceMeterShowError}
              helperText={serviceMeterHelperText}
              FormHelperTextProps={{
                className: serviceMeterShowError ? "error-text" : undefined,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">h</InputAdornment>,
              }}
              sx={{
                "& .MuiOutlinedInput-input": {
                  textAlign: "right",
                  direction: "ltr",
                  paddingRight: "8px",
                },
              }}
              inputProps={{
                step: "0.1",
                min: "0",
              }}
            />

            <TextField
              className={`input-field ${
                odometerShowError ? "error-border" : ""
              }`}
              label={getOdometerLabel(t)}
              type="text"
              inputMode="decimal"
              fullWidth
              variant="outlined"
              value={form.odometerValue}
              onChange={(e) => {
                const validatedInput = sanitizeNumericInputValue(
                  e.target.value,
                  form.odometerValue,
                );
                patchForm({
                  odometerValue: validatedInput,
                  odometerTouched: true,
                });
              }}
              onBlur={() => {
                const normalized = normalizeNumericOnBlur(
                  form.odometerValue,
                  NUMERIC_ODO_MAX,
                );
                patchForm({
                  odometerTouched: true,
                  ...(normalized !== form.odometerValue
                    ? { odometerValue: normalized }
                    : {}),
                });
              }}
              error={odometerShowError}
              helperText={odometerHelperText}
              FormHelperTextProps={{
                className: odometerShowError ? "error-text" : undefined,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">km</InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-input": {
                  textAlign: "right",
                  direction: "ltr",
                  paddingRight: "8px",
                },
              }}
              inputProps={{
                step: "0.1",
                min: "0",
              }}
            />
          </Box>
        </Box>
      </DataDialog>

      <DataDialog
        open={openCancelConfirm}
        setOpen={setOpenCancelConfirm}
        disableBackdropClick={true}
      >
        <Typography variant="body1" sx={{ mb: 3, fontWeight: 700 }}>
          {t("cancelAddingGroup")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => setOpenCancelConfirm(false)}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button className="btn btn-primary" onClick={handleClose}>
            {t("ok")}
          </Button>
        </Box>
      </DataDialog>

      <DataDialog
        title={t("errNetwork.title")}
        open={openFormNoMic}
        setOpen={setOpenFormNoMic}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("noMic")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormNoMic(false)}
              className="btn btn-primary ml-8"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("deleteRecording")}
        open={openFormDeleteRecording}
        setOpen={setOpenFormDeleteRecording}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDeleteRecording(false)}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={handleDeleteRecordingConfirmed}
              className="btn-delete"
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <SignatureDialog
        open={signatureDialogOpen}
        onClose={handleSignatureClose}
        onSend={handleSignatureSend}
        isSubmitting={isSubmitting}
      />

      {typeof document !== "undefined" &&
        viewerOpen &&
        selectedImage &&
        createPortal(
          <CustomImageViewer
            imageUrl={selectedImage}
            onClose={() => {
              setViewerOpen(false);
              setSelectedImage("");
            }}
          />,
          document.body,
        )}
    </>
  );
}
